import type * as brazeInstance from '@braze/web-sdk'
import { logger } from '../../logger'

export type BrazeInstanceType = typeof brazeInstance

let didInit = false
/**
 * Initializes the Braze SDK.
 * This function should be called on the client-side, for example, in a useEffect hook in a React component.
 * It ensures that the Braze Web SDK is only imported and initialized in a browser environment.
 */
export const initializeBraze = (language: string): void => {
  if (didInit) throw new Error('Braze SDK already initialized')

  try {
    if (!window.braze) {
      throw new Error(
        'window.braze is not available - Braze script may not have loaded'
      )
    }

    if (!window.KEYS?.brazeApiKey) {
      throw new Error('window.KEYS.brazeApiKey is not available')
    }

    if (!window.KEYS?.brazeSdkEndpoint) {
      throw new Error('window.KEYS.brazeSdkEndpoint is not available')
    }

    window.braze.initialize(window.KEYS.brazeApiKey, {
      baseUrl: window.KEYS.brazeSdkEndpoint,
    })
    window.braze.getUser()?.setLanguage(language)
    didInit = true
    logger.info('Braze SDK initialized and loaded')
  } catch (error) {
    logger.error('Failed to initialize Braze SDK', error, {
      errorMessage: (error as Error)?.message,
      errorName: (error as Error)?.name,
      errorStack: (error as Error)?.stack,
      windowBrazeExists: !!window.braze,
      brazeApiKeyExists: !!window.KEYS?.brazeApiKey,
      brazeSdkEndpointExists: !!window.KEYS?.brazeSdkEndpoint,
    })
  }
}

/**
 * Retrieves the Braze SDK instance.
 * If the SDK hasn't been initialized yet in a browser environment, it will attempt to initialize it.
 */
export function getBrazeInstance(shouldThrow?: true): BrazeInstanceType
export function getBrazeInstance(shouldThrow: false): BrazeInstanceType | null
export function getBrazeInstance(
  shouldThrow: boolean = true
): BrazeInstanceType | null {
  if (!didInit && shouldThrow) {
    throw new Error('Braze SDK not initialized. Call initializeBraze first.')
  }
  return didInit ? window.braze : null
}

// Default export for easier consumption or if previous code relied on a default import.
// Consumers will need to adapt to using the methods on this object.
export default {
  initializeBraze,
  getBrazeInstance,
}
